import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';

export interface ToastData {
  isShow?: boolean;
  severity?: string;
  errors?: {[key:string]: string};
  summary?: string;
  detail?: string;
  life?: number;
}

interface AppContextValues {
  toastData: ToastData | null;
  showToast:  React.Dispatch<React.SetStateAction<ToastData | null>>;
  isOnline: boolean;
}

export const AppContext = createContext({});
const AppProvider = ({ children }: { children: ReactNode }) => {

  const [toastData, showToast] = useState<ToastData | null>(null);

  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' && 'onLine' in navigator ? navigator.onLine : true
  );

  useEffect(() => {
    const checkConnection = async () => {
      // Fast optimistic update from the browser
      if (typeof navigator !== 'undefined' && 'onLine' in navigator) {
        if (!navigator.onLine) {
          setIsOnline(false);
          return; // no need to ping
        }
      }

      try {
        const res = await axios.get(route('health.proxy-ping'), {
          timeout: 2500,
          validateStatus: () => true,
          headers: { 'Cache-Control': 'no-cache' },
        });
        setIsOnline(res.status >= 200 && res.status < 300);
      } catch (e: any) {
        const isTimeout = e?.code === 'ECONNABORTED' || /timeout/i.test(e?.message || '');
        const isNetErr = e?.code === 'ERR_NETWORK';
        setIsOnline(!(isTimeout || isNetErr));
      }
    };
    checkConnection();
    const id = setInterval(checkConnection, 30000);
    return () => clearInterval(id);
  }, []);

  return (
    // this is the provider providing state
    <AppContext.Provider value={{ toastData, showToast, isOnline }}>
      {children}
    </AppContext.Provider>
  );
};

export function useAppContext(): AppContextValues {
  return useContext(AppContext) as AppContextValues;
}

export default AppProvider;
